/**
 * A date search must not hide what has no calendar.
 *
 * The Marketplace API's availability filter (`start`/`end`/`minDuration`) only answers for
 * listings with an availability plan. Monthly desks (`fixdesk`, purchase process), subscriptions
 * and the imported lounges have none, so a date in the search removed all of them — in Zürich
 * 52 of 75 offers (measured 2026-10-18). But a visitor who picks "10.–12. Nov." is not asking
 * "which offers have a plan", they are asking what they can work from on those days — and a
 * monthly desk or a lounge is bookable for them too.
 *
 * So a date search runs twice: the availability query for everything with a calendar, plus a
 * companion query for the listing types without one, same filters, no dates. The result list
 * shows the dated matches first, then the calendar-free offers (their card names the booking
 * model: "Monatlich", "Lounge").
 *
 * Which types have no calendar is read from the listing types, never from a hardcoded id: a
 * type has a calendar exactly when its transaction process is a booking process
 * (`isBookingProcess`: default-booking, cowork24-soft-booking). Purchase (fixdesk, Lounge),
 * subscription (cowork24-abo) and inquiry types are calendar-free. A type without process
 * information is left alone — it is not known to be calendar-free.
 *
 * Pure functions — the server render and the client agree on the plan.
 */

import { isBookingProcess } from '../transactions/transaction';
import { LISTING_TYPE_PARAM, parseListingTypeParam } from './searchQuickFilters';

const lower = id => `${id ?? ''}`.toLowerCase();

/**
 * The ids of the listing types without a calendar.
 *
 * @param {Array<Object>} listingTypes `config.listing.listingTypes` (normalised shape with
 *   `transactionType.process`)
 * @returns {Array<string>}
 */
export const calendarFreeListingTypeIds = (listingTypes = []) =>
  listingTypes
    .filter(lt => {
      const process = lt?.transactionType?.process;
      return lt?.listingType && process && !isBookingProcess(process);
    })
    .map(lt => `${lt.listingType}`);

/**
 * The search params (URL shape, as `buildListingsQueryParams` takes them) of the companion
 * query of a date search, or null when there is nothing to add: no date in the search, a
 * listing-type selection that holds no calendar-free type, or no such type at all.
 *
 * The companion keeps every other filter (place, bounds, category, price, features, keywords,
 * sort, places) and drops only the dates; `pub_listingType` becomes the calendar-free types,
 * narrowed to the visitor's own selection when there is one (the "Buchungsart" filter or
 * /s/:listingType). Compared case-insensitively — the hosted ids mix casing ("Lounge").
 *
 * @param {Object} searchParams search params incl. `dates`, maybe `listingTypePathParam`
 * @param {Array<Object>} listingTypes `config.listing.listingTypes`
 * @returns {Object|null}
 */
export const calendarFreeSearchParams = (searchParams = {}, listingTypes = []) => {
  const {
    dates,
    listingTypePathParam,
    isListingTypeVariant,
    [LISTING_TYPE_PARAM]: listingTypeParam,
    ...rest
  } = searchParams;
  if (!dates) {
    return null;
  }

  const calendarFree = calendarFreeListingTypeIds(listingTypes);
  const selected = isListingTypeVariant
    ? [`${listingTypePathParam}`]
    : parseListingTypeParam(listingTypeParam);
  const selectedLower = selected.map(lower);
  const scoped =
    selectedLower.length > 0
      ? calendarFree.filter(id => selectedLower.includes(lower(id)))
      : calendarFree;

  return scoped.length > 0 ? { ...rest, [LISTING_TYPE_PARAM]: scoped.join(',') } : null;
};
